import { useEffect, useState } from "react";

import { ClinicalWaitingList } from "@/Common/Api/Lists/Dto/ClinicalWaitingList";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ListModuleItemType } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { ListHelper } from "../Helpers/ListHelper";
import { IListModuleItem } from "../Models/UI/IListModuleItem";
import { AddNewListPopupControl } from "./AddNewListPopupControl";

import styles from "./MultiListsTreeControl.module.scss";

interface IMultiListsTreeControlProps {
    clinicalWaitingList?: Immutable.List<ClinicalWaitingList>;
    selectedListId?: string;
    searchTextValue?: string
    listItemCallback?: () => void;
    searchListCallback?: (searchValue: string) => void;
}

function MultiListsTreeControl(props: IMultiListsTreeControlProps): JSX.Element {
    const { getText } = useTranslation();

    const [listItems, setListItems] = useState<IListModuleItem[]>([]);
    const [showAddNewListPopup, setShowAddNewListPopup] = useState<boolean>(false);
    const [selectedListItem, setSelectedListItem] = useState(props.selectedListId);
    const [searchText, setSearchText] = useState(props.searchTextValue);

    function combineAllListItems(): void {
        let listItems: IListModuleItem[] = (props.clinicalWaitingList?.toArray() ?? [])
            .map(clinic => ({
                Id: clinic.Id,
                Name: ListHelper.getNameFormat(clinic.Name),
                PatientsCount: clinic.Count,
                Type: ListModuleItemType.List
            }));

        listItems = listItems
            .filter(item => !!item.Name)
            .sortByProperty(item => item.Name.toLowerCase());

        setListItems(listItems);
        if (selectedListItem)
            renderPatientGroupList();
    }

    useEffect(() => {
        combineAllListItems();
        $(window).trigger("resize");
    }, [props.clinicalWaitingList, selectedListItem]);

    function onSaveNewList(listName: string): void {
        setShowAddNewListPopup(!showAddNewListPopup);
        sessionStore.dispatch(LocalOperations.Lists.AddNewList_Popup_Ok, { value: listName });
        props.listItemCallback();
    }

    function renderPatientGroupList(): void {
        if (!listItems)
            return;

        const listItem = listItems.filter(item => item.Id === props.selectedListId);
        if (listItem.length > 0)
            onClickCalback(listItem.firstOrDefault());
    }

    function onListItemClick(list: IListModuleItem): void {
        sessionStore.dispatch(
            LocalOperations.Lists.List_Selected,
            {
                listId: list.Id,
                listName: list.Name
            }
        );

        onClickCalback(list);
        setSelectedListItem(list.Id);
    }

    function onClickCalback(list: IListModuleItem): void {
        if (list.Type === ListModuleItemType.List) {
            props.listItemCallback();
        }
    }

    function searchLists(searchText: string): void {
        setSearchText(searchText);
        sessionStore.dispatch(LocalOperations.Lists.SearchText_Change, { searchText: searchText, pageSize: 25 });
    }

    return (
        <div className={`fxheight transition full-border ${styles.multiListsSearch}`}>
            <div className={styles.listSearch}>
                <div className="flex-container s">
                    <div className={`search-field ${styles.searchFieldContainer}`}>
                        <div className="gf">
                            <span className="glyphicon glyphicon-search" />
                        </div>
                        <div>
                            <TextboxControl
                                onMountFocus={true}
                                debounceMs={400}
                                value={searchText}
                                name="text_search_users"
                                className="fullWidth"
                                operation={null}
                                placeholder={getText("SearchLabel")}
                                autoComplete="off"
                                onChange={text => searchLists(text)}
                                autoCompleteItems={null}
                            />
                        </div>
                    </div>
                </div>
                <div className={`${styles.listCreateOptions} flex-container`}>
                    <div className={`flx-col ${styles.leftAlign}`}>
                        <a onClick={() => setShowAddNewListPopup(!showAddNewListPopup)}>{`[+] ${getText("PatientGroup_NewListLabel")}`}</a>
                    </div>
                </div>
            </div>
            <div className={styles.listsSearchResults}>
                <div className="pointer">
                    {listItems?.filter(list => !!list.Name).map((item: IListModuleItem, i: number) => {
                        {
                            return (
                                <div
                                    className={`${styles.listSearchItem} ${props.selectedListId && item.Id === props.selectedListId ? styles.Selected : ""}`}
                                    id={`__listItem ${i}`}
                                    key={`${i} ___listItem`}
                                    onClick={() => onListItemClick(item)}
                                >
                                    <div className="flex-container">
                                        <div className={`flx-col ${styles.leftAlign} ${styles.listSearchEllipsis}`}>
                                            <strong title={item.Name}>{item.Name}</strong>
                                        </div>
                                    </div>
                                    <div>
                                        {`${item.PatientsCount?.toString()} ${getText("PatientGroup_PatientsLabel")}`}
                                    </div>
                                </div>
                            );
                        }
                    })}
                </div>
            </div>
            {showAddNewListPopup && (
                <AddNewListPopupControl
                    ContainerId="AddNewListDialog"
                    onCancel={() => { setShowAddNewListPopup(!showAddNewListPopup); }}
                    onSave={listName => onSaveNewList(listName)}
                />
            )}
        </div>
    );
}

export { MultiListsTreeControl };
